import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Instagram, Menu, X, Info, Clock } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const MAROON = "#6B2B3A";
const TAUPE = "#EDE6DA";
const NAME = "Smiles Dental Care";
const FB = "https://www.facebook.com/p/Smiles-Dental-Care-100063740413252/";
const IG = "https://www.instagram.com/smiles.dentalcare/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function SmilesConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Our Team", "#team"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: TAUPE, color: "#2A241E", fontFamily: "'Mulish', sans-serif" }} data-testid="smiles-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Mulish:wght@400;500;600;700&display=swap'); .sm-serif{font-family:'Libre Baskerville',serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-gray-900 text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-[#FBF9F5] shadow-sm">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="sm-logo" className="flex items-center gap-3">
            <img src="/dental/smiles-dental-concept/logo.jpg" alt={NAME} className="h-11 object-contain" />
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#4A3F35] hover:text-[#6B2B3A] transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="sm-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: MAROON }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="sm-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-[#FBF9F5]">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — the clinic's own promo banner, framed cleanly */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-6" data-testid="sm-hero">
        <motion.div {...fadeUp} className="text-center mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: MAROON }}>Dumaguete City · Since 2008</span>
          <h1 className="mt-4 text-5xl sm:text-6xl sm-serif" style={{ color: MAROON }}>Excellence in Dental Care</h1>
          <p className="mt-4 text-lg text-[#4A3F35]/80">Creating healthy, confident smiles under Dr. Mae Rubio, Director &amp; Dentist.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="#visit" data-testid="sm-hero-book" className="px-8 py-3.5 text-sm font-semibold text-white" style={{ background: MAROON }}>Book a Visit</a>
            <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="sm-hero-message" className="px-8 py-3.5 text-sm font-semibold border-2" style={{ borderColor: MAROON, color: MAROON }}>Message Us</a>
          </div>
        </motion.div>
        <motion.img {...fadeUp} src="/dental/smiles-dental-concept/promo.png" alt="Smiles Dental Care — book your appointment today" className="w-full object-contain shadow-md" />
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl sm-serif leading-relaxed" style={{ color: MAROON }}>
          Smiles Dental Care has offered general dentistry and orthodontics from Dr. V. Locsin Street, Dumaguete City, since 2008.
        </motion.p>
        <motion.div {...fadeUp} className="mt-8 inline-flex items-center gap-3 text-sm font-medium text-[#4A3F35]">
          <Clock size={18} style={{ color: MAROON }} /> Monday – Saturday, 9am – 5pm
        </motion.div>
      </section>

      {/* TEAM — the clinic's own real "Meet the Team" graphic */}
      <section id="team" className="py-20" style={{ background: "#FBF9F5" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: MAROON }}>Meet the Team</span>
          </motion.div>
          <motion.img {...fadeUp} src="/dental/smiles-dental-concept/team.jpg" alt="Meet the team at Smiles Dental: Dr. Mae Rubio, Dr. Seleena Lim, Joanna, Lyka" className="w-full object-contain shadow-md" />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.img {...fadeUp} src="/dental/smiles-dental-concept/team-group.jpg" alt="The Smiles Dental Care team" className="w-full max-w-md mx-auto object-cover shadow-lg" />
        <motion.p {...fadeUp} className="mt-6 text-[#4A3F35]/70">Our full team, together at the clinic</motion.p>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="sm-serif text-3xl text-center mb-12" style={{ color: MAROON }}>What Patients Say</motion.h2>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { text: "Very satisfied; praises the dentist and assistants as kind and gentle and highly recommends the clinic.", author: "Angel S. Chong", source: "Facebook" },
            { text: "Praises Dr. Mae for attention to detail, a gentle approach and commitment to patient comfort.", author: "Facebook mentions" },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`sm-testimonial-${i}`} className="bg-white p-7 shadow-sm">
              <p className="text-[#3A322A]/85 leading-relaxed">"{q.text}"</p>
              <p className="mt-4 text-sm font-semibold" style={{ color: MAROON }}>{q.author}{q.source ? ` · ${q.source}` : ""}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-20" style={{ background: MAROON }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="sm-serif text-3xl text-white">Visit Us</h2>
            <ul className="mt-7 space-y-5 text-white/85">
              <li className="flex items-start gap-4"><MapPin size={20} className="mt-0.5" />Door 5, A.T. Locsin Building, Dr. V. Locsin St., Dumaguete City 6200</li>
              <li className="flex items-start gap-4"><Phone size={20} className="mt-0.5" /><a href="tel:+639565210042" className="hover:underline">+63 956 521 0042</a></li>
              <li className="flex items-start gap-4"><Mail size={20} className="mt-0.5" /><a href="mailto:smilesdentalcare.team@gmail.com" className="hover:underline">smilesdentalcare.team@gmail.com</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="sm-facebook-link" className="hover:underline">Smiles Dental Care</a></li>
              <li className="flex items-start gap-4"><Instagram size={20} className="mt-0.5" /><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="sm-instagram-link" className="hover:underline">smiles.dentalcare</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px] shadow-md">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("AT Locsin Building Dr V Locsin St Dumaguete City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-[#FBF9F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#6B6055]">
          <p className="sm-serif text-base" style={{ color: MAROON }}>Smiles Dental Care</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: MAROON }}>Message Smiles Dental</a>
      <ConceptCTAPill slug="smiles-dental-concept" />
    </div>
  );
}
